import React, { useMemo } from "react";
import AppSelect from "@/components/shared/AppSelect";

/**
 * Componente que maneja los modales relacionados con la creación de nuevos equipos
 *
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.showNewEquipoModal - Controla la visibilidad del modal inicial
 * @param {Function} props.setShowNewEquipoModal - Función para cambiar la visibilidad del primer modal
 * @param {boolean} props.showNewEquipoForm - Controla la visibilidad del formulario
 * @param {Function} props.setShowNewEquipoForm - Función para cambiar la visibilidad del formulario
 * @param {string} props.codeToLink - Código escaneado que se asociará al nuevo equipo
 * @param {Object} props.newEquipo - Datos del nuevo equipo
 * @param {Function} props.setNewEquipo - Función para actualizar los datos del equipo
 * @param {boolean} props.creatingEquipo - Indica si está en proceso de creación
 * @param {Array} props.grupos - Lista de grupos disponibles
 * @param {Array} props.tipos - Lista de tipos disponibles
 * @param {Array} props.medidas - Lista de medidas disponibles
 * @param {Array} props.unidades - Lista de unidades de medida disponibles
 * @param {Function} props.validateNewEquipoForm - Función para validar el formulario
 * @param {Function} props.createNewEquipo - Función para enviar la solicitud de creación
 * @returns {JSX.Element|null} Los modales de creación de equipo
 */
const NewEquipoModal = ({
  showNewEquipoModal,
  setShowNewEquipoModal,
  showNewEquipoForm,
  setShowNewEquipoForm,
  codeToLink,
  newEquipo,
  setNewEquipo,
  creatingEquipo,
  grupos,
  tipos,
  medidas,
  unidades,
  marcas,
  validateNewEquipoForm,
  createNewEquipo,
}) => {
  const grupoOptions = useMemo(
    () => [
      { value: "", label: "Seleccione un grupo" },
      ...grupos.map((grupo) => ({
        value: String(grupo.idgrupoinstalacion),
        label: grupo.grupoinstalacion,
      })),
    ],
    [grupos],
  );

  const tipoOptions = useMemo(
    () => [
      { value: "", label: "Seleccione un tipo" },
      ...tipos.map((tipo) => ({
        value: String(tipo.idtipoinstalacion),
        label: tipo.tipoinstalacion,
      })),
    ],
    [tipos],
  );

  const medidaOptions = useMemo(
    () => [
      { value: "", label: "Seleccione una medida" },
      { value: "sin-medida", label: "Sin medida" },
      ...medidas.map((medida) => ({
        value: String(medida.idmedidainstalacion),
        label: medida.medidainstalacion,
      })),
    ],
    [medidas],
  );

  const unidadOptions = useMemo(
    () => [
      { value: "", label: "Seleccione una unidad" },
      ...unidades.map((unidad) => ({
        value: String(unidad.idunidadmedida),
        label: unidad.unidadmedida,
      })),
    ],
    [unidades],
  );

  const marcaOptions = useMemo(
    () => [
      { value: "", label: "Sin marca" },
      ...marcas.map((marca) => ({
        value: String(marca.idmarca),
        label: marca.marca,
      })),
    ],
    [marcas],
  );

  return (
    <>
      {/* Modal para código no encontrado */}
      {showNewEquipoModal && (
        <div className="scanner-modal-overlay">
          <div className="scanner-modal">
            <div
              className="scanner-modal-header"
              style={{ backgroundColor: "#f8d7da" }}
            >
              <h2>Código no reconocido</h2>
              <button
                className="scanner-modal-close"
                onClick={() => setShowNewEquipoModal(false)}
              >
                ×
              </button>
            </div>

            <div className="scanner-modal-content">
              <p>
                El código <strong>"{codeToLink}"</strong> no está asociado a
                ningún equipo existente.
              </p>
              <p>¿Desea crear un nuevo equipo con este código?</p>
            </div>

            <div className="scanner-modal-footer">
              <button
                className="scanner-modal-button cancel"
                onClick={() => setShowNewEquipoModal(false)}
              >
                Cancelar
              </button>
              <button
                className="scanner-modal-button create"
                onClick={() => {
                  setShowNewEquipoModal(false);
                  setShowNewEquipoForm(true);
                  setNewEquipo({
                    ...newEquipo,
                    codigo_fabrica: codeToLink,
                  });
                }}
              >
                <i className="fa-solid fa-plus"></i> Crear nuevo equipo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para crear nuevo equipo */}
      {showNewEquipoForm && (
        <div className="scanner-modal-overlay">
          <div className="scanner-modal scanner-modal-large">
            <div
              className="scanner-modal-header"
              style={{ backgroundColor: "#28a745" }}
            >
              <h2>Crear nuevo equipo</h2>
              <button
                className="scanner-modal-close"
                onClick={() => setShowNewEquipoForm(false)}
              >
                ×
              </button>
            </div>

            <div className="scanner-modal-content">
              <p>
                Complete la información del nuevo equipo. Se asociará con el
                código: <strong>{codeToLink}</strong>
              </p>

              <form className="scanner-new-equipo-form">
                <div className="form-group">
                  <label htmlFor="idgrupoinstalacion">Grupo:</label>
                  <AppSelect
                    id="idgrupoinstalacion"
                    options={grupoOptions}
                    value={newEquipo.idgrupoinstalacion}
                    onChange={(val) =>
                      setNewEquipo({ ...newEquipo, idgrupoinstalacion: val })
                    }
                    variant="modal"
                    menuPortalTarget={document.body}
                    placeholder="Seleccione un grupo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="idtipoinstalacion">Tipo:</label>
                  <AppSelect
                    id="idtipoinstalacion"
                    options={tipoOptions}
                    value={newEquipo.idtipoinstalacion}
                    onChange={(val) =>
                      setNewEquipo({ ...newEquipo, idtipoinstalacion: val })
                    }
                    variant="modal"
                    menuPortalTarget={document.body}
                    placeholder="Seleccione un tipo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="idmarca">Marca:</label>
                  <AppSelect
                    id="idmarca"
                    options={marcaOptions}
                    value={newEquipo.idmarca}
                    onChange={(val) => setNewEquipo({ ...newEquipo, idmarca: val })}
                    variant="modal"
                    menuPortalTarget={document.body}
                    placeholder="Sin marca"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="descripcion">Descripción:</label>
                  <input
                    type="text"
                    id="descripcion"
                    value={newEquipo.descripcion}
                    onChange={(e) =>
                      setNewEquipo({
                        ...newEquipo,
                        descripcion: e.target.value,
                      })
                    }
                    required
                    placeholder="Descripción del equipo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="idmedidainstalacion">Medida:</label>
                  <AppSelect
                    id="idmedidainstalacion"
                    options={medidaOptions}
                    value={newEquipo.idmedidainstalacion}
                    onChange={(val) =>
                      setNewEquipo({ ...newEquipo, idmedidainstalacion: val })
                    }
                    variant="modal"
                    menuPortalTarget={document.body}
                    placeholder="Seleccione una medida"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="idunidadmedida">Unidad de medida:</label>
                  <AppSelect
                    id="idunidadmedida"
                    options={unidadOptions}
                    value={newEquipo.idunidadmedida}
                    onChange={(val) =>
                      setNewEquipo({ ...newEquipo, idunidadmedida: val })
                    }
                    variant="modal"
                    menuPortalTarget={document.body}
                    placeholder="Seleccione una unidad"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="stock">Stock inicial:</label>
                  <input
                    type="number"
                    id="stock"
                    value={newEquipo.stock}
                    onChange={(e) =>
                      setNewEquipo({
                        ...newEquipo,
                        stock: Math.max(1, e.target.value),
                      })
                    }
                    min="1"
                    required
                  />
                </div>
              </form>
            </div>

            <div className="scanner-modal-footer">
              <button
                className="scanner-modal-button cancel"
                onClick={() => setShowNewEquipoForm(false)}
                disabled={creatingEquipo}
              >
                Cancelar
              </button>
              <button
                className="scanner-modal-button confirm confirm-add"
                onClick={createNewEquipo}
                disabled={creatingEquipo || !validateNewEquipoForm()}
              >
                {creatingEquipo ? (
                  <>
                    <div className="scanner-spinner button-spinner"></div>{" "}
                    Creando...
                  </>
                ) : (
                  <>Crear equipo</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(NewEquipoModal);
